import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

export interface BreadcrumbItem {
  name: string;
  to?: string;
}

interface SidebarMenu {
  id?: number;
  code: string;
  name: string;
  route?: string | null;
  type?: string;
  children?: SidebarMenu[];
}

export const useBreadcrumbFromSidebar = (): BreadcrumbItem[] => {
  const { pathname } = useLocation();
  const { categories, menus } = useSelector(
    (state: RootState) => state.sidebar
  );

  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);

  const normalizePath = (path: string): string => {
    let clean = path.split('?')[0].split('#')[0];

    clean = clean.replace(/\/(page|edit|view|print|details)\/\d+$/, '/$1');
    clean = clean.replace(/\/\d+$/, '');

    return clean;
  };

  const findMenuPath = (
    menuList: SidebarMenu[],
    currentPath: string,
    parents: BreadcrumbItem[] = []
  ): BreadcrumbItem[] | null => {
    for (const menu of menuList) {
      const currentItem: BreadcrumbItem = {
        name: menu.name,
        to: menu.route || undefined,
      };

      if (menu.route && currentPath === normalizePath(menu.route)) {
        return [...parents, currentItem];
      }

      if (menu.children && menu.children.length > 0) {
        const found = findMenuPath(menu.children, currentPath, [
          ...parents,
          currentItem,
        ]);

        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  useEffect(() => {
    const currentPath = normalizePath(pathname);
    let newBreadcrumb: BreadcrumbItem[] = [];

    for (const category of categories) {
      for (const module of category.modules || []) {
        const moduleMenus: SidebarMenu[] = menus[module.code] || [];

        if (!module.hasChildren && module.route && currentPath === module.route) {
          newBreadcrumb = [
            {
              name: module.name,
              to: module.route,
            },
          ];
          break;
        }

        if (module.hasChildren && moduleMenus.length > 0) {
          const menuPath = findMenuPath(moduleMenus, currentPath);

          if (menuPath) {
            newBreadcrumb = [
              {
                name: module.name,
                to: module.route || undefined,
              },
              ...menuPath,
            ];
            break;
          }
        }
      }

      if (newBreadcrumb.length > 0) {
        break;
      }
    }

    if (newBreadcrumb.length > 0) {
      setBreadcrumb(newBreadcrumb);
      localStorage.setItem('breadcrumb-cache', JSON.stringify(newBreadcrumb));
    } else {
      const cached = localStorage.getItem('breadcrumb-cache');

      if (cached) {
        try {
          setBreadcrumb(JSON.parse(cached));
        } catch {
          localStorage.removeItem('breadcrumb-cache');
        }
      }
    }
  }, [pathname, categories, menus]);

  return breadcrumb;
};